import { Controller } from "@hotwired/stimulus"
import {
  buildNoticeRow,
  collectFromDataTransfer,
  uploadEntries,
  refreshFolderTotals,
  formatFileSize,
} from "../../helpers/document_uploads"

// Accepts OS file/folder drags dropped anywhere on the documents section:
// dropping on the section body uploads to the root level, dropping on a folder
// row uploads into that folder (nested dropped folders are created server-side
// via relative_path). One instance per section — folder rows are resolved by
// event delegation through data-external-drop-* attributes, so lazily loaded
// subfolder rows work without their own controller instances.
export default class extends Controller {
  static targets = ["dropRegion", "panel", "previews", "template"]
  static values = {
    url: String,
    totalsUrl: { type: String, default: "" },
    resolvePathsUrl: { type: String, default: "" },
    canCreateFolders: { type: Boolean, default: true },
    maxDepth: Number,
    fileTooLargeMessage: { type: String, default: "" },
    uploadFailedMessage: { type: String, default: "" },
    lockedMessage: { type: String, default: "" },
    maxDepthMessage: { type: String, default: "" },
    tooManyFilesMessage: { type: String, default: "" },
    folderCreationMessage: { type: String, default: "" },
  }

  connect() {
    this.dragDepth = 0
    this.highlightedRow = null
    this.clearTimer = null
    this.hidePanelTimer = null
  }

  disconnect() {
    clearTimeout(this.clearTimer)
    clearTimeout(this.hidePanelTimer)
  }

  dragenter(event) {
    if (!this.externalDrag(event)) return

    event.preventDefault()
    this.dragDepth++
    clearTimeout(this.clearTimer)
  }

  dragover(event) {
    if (!this.externalDrag(event)) return

    event.preventDefault()
    clearTimeout(this.clearTimer)
    this.updateHighlight(this.rowFromEvent(event))
  }

  dragleave(event) {
    if (!this.externalDrag(event)) return

    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) {
      // Debounced: dragleave/dragenter pairs fire on every child boundary and
      // would otherwise flicker the highlight.
      clearTimeout(this.clearTimer)
      this.clearTimer = setTimeout(() => this.clearHighlights(), 100)
    }
  }

  async drop(event) {
    if (!this.externalDrag(event)) return

    event.preventDefault()
    this.dragDepth = 0
    const row = this.rowFromEvent(event)
    this.clearHighlights()

    const entries = await collectFromDataTransfer(event.dataTransfer)
    if (entries.length === 0) return

    this.showPanel()

    // The target folder is locked for THIS viewer: refuse up front. Waiting for the
    // server's 422 would first transfer every byte to storage (orphan blobs included).
    if (row?.dataset.externalDropLocked === "true") {
      entries.forEach((entry) => {
        const preview = this.createPreview(entry.file, entry.relativePath)
        this.showUploadError(preview, this.lockedMessageValue)
      })
      return
    }

    let uploadable = []
    entries.forEach((entry) => {
      if (this.exceedsMaxDepth(entry.relativePath, row)) {
        const preview = this.createPreview(entry.file, entry.relativePath)
        this.showUploadError(preview, this.maxDepthMessageValue)
        return
      }
      uploadable.push(entry)
    })

    // No folder-create permission: folder drops are only allowed into ALREADY existing
    // structure, so ask the server which dropped paths exist and refuse the rest before
    // uploading any bytes. The server-side per-file 403 stays authoritative.
    if (!this.canCreateFoldersValue) {
      uploadable = await this.rejectUncreatablePaths(uploadable, row)
    }

    if (uploadable.length === 0) return

    await uploadEntries(uploadable, {
      url: this.urlValue,
      folderId: row?.dataset.externalDropFolderId ?? null,
      // The recursive folder totals are refreshed ONCE below, not per uploaded file.
      deferTotals: true,
      fileTooLargeMessage: this.fileTooLargeMessageValue,
      uploadFailedMessage: this.uploadFailedMessageValue,
      onPreview: (file, relativePath) => this.createPreview(file, relativePath),
      onProgress: (preview, percent) => this.updateProgress(preview, percent),
      onSuccess: (preview) => this.showSuccess(preview),
      onError: (preview, error) => this.showUploadError(preview, error),
      onTruncated: (skipped, max) => this.showTruncationNotice(skipped, max),
    })

    await refreshFolderTotals(this.totalsUrlValue)
  }

  // Asks the server which dropped folder paths already exist; entries whose path is
  // missing get an immediate error preview instead of a full upload that ends in a 403.
  // On any preflight failure everything proceeds — the server check is authoritative.
  async rejectUncreatablePaths(entries, row) {
    const paths = [...new Set(entries.map((entry) => entry.relativePath).filter(Boolean))]
    if (paths.length === 0 || !this.resolvePathsUrlValue) return entries

    let existing
    try {
      const url = new URL(this.resolvePathsUrlValue, window.location.origin)
      paths.forEach((path) => url.searchParams.append("paths[]", path))
      const folderId = row?.dataset.externalDropFolderId
      if (folderId) url.searchParams.set("document_folder_id", folderId)

      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) return entries
      existing = await response.json()
    } catch (_e) {
      return entries
    }

    return entries.filter((entry) => {
      if (!entry.relativePath || existing[entry.relativePath]) return true

      const preview = this.createPreview(entry.file, entry.relativePath)
      this.showUploadError(preview, this.folderCreationMessageValue)
      return false
    })
  }

  showTruncationNotice(skipped, max) {
    const message = this.tooManyFilesMessageValue
      .replace("%{count}", skipped)
      .replace("%{max}", max)

    this.previewsTarget.appendChild(buildNoticeRow(this.templateTarget, message))
  }

  // Only handle OS drags carrying files; internal row/file drags (SortableJS
  // reordering, move-to-folder) mark their element with data-dragging.
  externalDrag(event) {
    if (!event.dataTransfer?.types?.includes("Files")) return false
    // Scoped to the section and not the document: dragover fires continuously,
    // and the internal drags this guards against all happen inside it.
    if (this.element.querySelector("[data-dragging='true']")) return false

    return true
  }

  rowFromEvent(event) {
    if (!(event.target instanceof Element)) return null

    return event.target.closest("[data-external-drop-folder-id]")
  }

  // Depth of the deepest folder a dropped path would create must stay within
  // MAX_DEPTH. Server-side validation is authoritative; this just avoids
  // uploading bytes for files that are certain to be rejected.
  exceedsMaxDepth(relativePath, row) {
    const segments = relativePath ? relativePath.split("/").filter(Boolean).length : 0
    if (segments === 0) return false

    const rowDepth = row ? parseInt(row.dataset.externalDropDepth, 10) : -1
    return rowDepth + segments > this.maxDepthValue
  }

  updateHighlight(row) {
    if (row === this.highlightedRow) return

    this.clearHighlights()
    this.highlightedRow = row

    if (row) {
      if (row.dataset.externalDropLocked === "true") {
        row.classList.add("ring-2", "ring-red-300", "ring-inset")
      } else {
        row.classList.add("ring-2", "ring-green-400", "ring-inset", "bg-green-50")
      }
    } else if (this.hasDropRegionTarget) {
      this.dropRegionTarget.classList.add("ring-2", "ring-green-400", "ring-offset-2", "rounded-lg")
    }
  }

  clearHighlights() {
    if (this.highlightedRow) {
      this.highlightedRow.classList.remove(
        "ring-2",
        "ring-green-400",
        "ring-red-300",
        "ring-inset",
        "bg-green-50",
      )
      this.highlightedRow = null
    }
    if (this.hasDropRegionTarget) {
      this.dropRegionTarget.classList.remove(
        "ring-2",
        "ring-green-400",
        "ring-offset-2",
        "rounded-lg",
      )
    }
  }

  showPanel() {
    clearTimeout(this.hidePanelTimer)
    this.panelTarget.classList.remove("hidden")
  }

  createPreview(file, relativePath) {
    const template = this.templateTarget.content.cloneNode(true)
    const container = template.querySelector("[data-upload-item]")

    const displayName = relativePath ? `${relativePath}/${file.name}` : file.name
    container.querySelector("[data-filename]").textContent = displayName
    container.querySelector("[data-filesize]").textContent = formatFileSize(file.size)

    this.previewsTarget.appendChild(container)
    return container
  }

  updateProgress(preview, percent) {
    const progressBar = preview.querySelector("[data-progress]")
    if (progressBar) {
      progressBar.style.width = `${percent}%`
    }
  }

  showSuccess(preview) {
    preview.querySelector(".uploading").classList.add("hidden")
    preview.querySelector(".completed").classList.remove("hidden")

    const progressBar = preview.querySelector("[data-progress]")
    if (progressBar) {
      progressBar.style.width = "100%"
    }

    setTimeout(() => {
      preview.remove()
      this.hidePanelWhenEmpty()
    }, 1000)
  }

  showUploadError(preview, error) {
    preview.querySelector(".uploading").classList.add("hidden")
    preview.querySelector(".error").classList.remove("hidden")

    const message = typeof error === "string" ? error : this.uploadFailedMessageValue
    const messageEl = preview.querySelector("[data-filesize]")
    if (messageEl && message) {
      messageEl.textContent = message
      messageEl.classList.add("text-red-600")
      messageEl.classList.remove("text-gray-500")
    }
    console.error("Upload error:", error)
  }

  removeFile(event) {
    const preview = event.target.closest("[data-upload-item]")
    preview.remove()
    this.hidePanelWhenEmpty()
  }

  hidePanelWhenEmpty() {
    if (this.previewsTarget.children.length > 0) return

    clearTimeout(this.hidePanelTimer)
    this.hidePanelTimer = setTimeout(() => {
      if (this.previewsTarget.children.length === 0) {
        this.panelTarget.classList.add("hidden")
      }
    }, 400)
  }
}
